import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Autocomplete, Box, Breadcrumbs, Button, TextField, Typography,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import AppShell from './components/AppShell';
import api from './services/api';
import DisqualifyStatus from './disqualify/DisqualifyStatus';
import useDisqualify from './disqualify/useDisqualify';

const DuplicateRemoval = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([null]);
  const [duplicates, setDuplicates] = useState([]);
  const [nDuplicates, setNDuplicates] = useState(0);
  const [numberResponses, setNumberResponses] = useState(0);
  const disq = useDisqualify(surveyId);

  useEffect(() => {
    api.get(`/surveys/${surveyId}/`).then((res) => {
      setSurvey(res.data);
      api.get('/alchemer/questions/', { params: { survey_id: res.data.alchemer_survey_id } })
        .then((qRes) => {
          setQuestions(qRes.data.data.map((q) => ({ id: q.id, title: q.title.English })));
        });
    });
  }, [surveyId]);

  const handleSubmit = async () => {
    const selected = selectedQuestions.filter((q) => q != null);
    const payload = { question1: selected[0], question2: selected[1], question3: selected[2], question4: selected[3] };
    const res = await api.post(`/surveys/${surveyId}/find-duplicates/`, payload);
    setDuplicates(res.data.duplicates);
    setNDuplicates(res.data.n_duplicates);
    setNumberResponses(res.data.number_responses);
  };

  const handleDisqualify = async () => {
    await disq.run(duplicates);
  };

  const handleDownloadCSV = () => {
    if (!duplicates.length) { alert('No duplicate responses to download.'); return; }
    const csv = 'data:text/csv;charset=utf-8,SurveyID\n' + duplicates.join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', 'duplicate_responses.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!survey) return null;

  return (
    <AppShell maxWidth="sm">
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/project/${survey.project}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.project_name}</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>{survey.name}</Link>
        <Typography color="textPrimary">Duplicate Removal</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <ContentCopyRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Duplicate Removal
        </Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Select questions to compare for duplicate responses.
      </Typography>

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {selectedQuestions.map((questionId, index) => (
          <Box key={index}>
            <Autocomplete
              options={questions}
              getOptionLabel={(o) => o.title}
              value={questions.find((q) => q.id === questionId) || null}
              onChange={(_, newVal) => {
                const updated = [...selectedQuestions];
                updated[index] = newVal ? newVal.id : null;
                setSelectedQuestions(updated);
              }}
              renderInput={(params) => <TextField {...params} label={`Question ${index + 1}`} />}
            />
            {selectedQuestions.length > 1 && (
              <Button size="small" onClick={() => setSelectedQuestions(selectedQuestions.filter((_, i) => i !== index))}>
                Remove
              </Button>
            )}
          </Box>
        ))}
        {selectedQuestions.length < 4 && (
          <Button onClick={() => setSelectedQuestions([...selectedQuestions, null])} sx={{ alignSelf: 'flex-start' }}>
            Add Another Question
          </Button>
        )}

        <Button variant="contained" onClick={handleSubmit} sx={{ borderRadius: 2, textTransform: 'none' }}>
          Check Duplicates
        </Button>

        {duplicates.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6">
              Duplicates found: {nDuplicates} / {numberResponses} responses
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Button variant="contained" color="secondary" onClick={handleDisqualify} sx={{ textTransform: 'none', borderRadius: 2 }}>
                Disqualify Duplicates
              </Button>
              <Button variant="outlined" onClick={handleDownloadCSV} sx={{ textTransform: 'none', borderRadius: 2 }}>
                Download CSV
              </Button>
            </Box>
          </Box>
        )}

        <DisqualifyStatus state={disq} surveyId={surveyId} />
      </Box>
    </AppShell>
  );
};

export default DuplicateRemoval;
